import React from 'react';
import { signOut } from 'firebase/auth';
import { Linking, View, Text, Pressable, StyleSheet } from 'react-native';
import { DrawerContentScrollView } from '@react-navigation/drawer';
import { CommonActions } from '@react-navigation/native';
import { Octicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { Footer } from './style';
import THEME from '../../config/theme';
import { auth } from '../../services/firebase';
import { FooterText, Container } from '../../config/theme/globalStyles';
import { drawerClosedLinks, drawerOpenedLinks } from '../../config/data';
import { A } from '@expo/html-elements';
import Constants from 'expo-constants';
import { useCustomTheme } from '../../contexts/useCustomTheme';
import { useAuthUser } from '../../contexts/useAuthUser';
import pkg from '../../../package.json';

// Versão exibida no rodapé. Fonte: package.json (bump via `npm version` no CI),
// lido direto no bundle — NÃO por Constants.manifest.extra. Motivo: no deploy
// centralizado o passo "Download custom static files" baixa a pasta do cliente
// do Storage por cima da raiz do projeto e substitui o app.config.js por uma
// cópia antiga (sem `appVersion`), então o manifesto do Expo não é confiável.
// O commit só aparece quando o CI o injeta (WEB_COMMIT_SHA) — em produção fica vazio.
const APP_VERSION = pkg?.version || Constants?.manifest?.extra?.appVersion;
const COMMIT_SHA = Constants?.manifest?.extra?.commitSha;
const VERSION_LABEL = APP_VERSION
  ? `versão ${APP_VERSION}${COMMIT_SHA ? ` (${String(COMMIT_SHA).slice(0, 7)})` : ''}`
  : null;

// Converte um hex (#rgb / #rrggbb) em rgba(); retorna null quando a cor do cliente
// não for hex (ex.: já vem como rgb()/nome), para escolhermos um fallback neutro.
const hexToRgba = (hex, alpha) => {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return null;
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Agrupamento do menu na visão do ADMINISTRADOR. A ordem dos itens dentro de cada
// grupo (e dos grupos) segue o pedido do produto.
const ADMIN_GROUPS = [
  { title: 'Personalização', items: ['Marca', 'Tela de login', 'App Mobile'] },
  { title: 'Aplicativo', items: ['Produtos', 'Alunos', 'Comentários'] },
  { title: 'Configurações', items: ['Minha conta', 'Funcionalidades'] },
  {
    title: 'Suporte',
    items: ['Termos de Uso'],
    links: [
      {
        title: 'Central de ajuda',
        url: 'https://blix-docs.scrollhelp.site/sac/comece-por-aqui-clientes-novos',
        icon: 'question',
      },
    ],
  },
];

// Rotas que só existem no drawer do admin — usadas para detectar a visão admin de
// forma síncrona (sem depender do fetch do usuário) e evitar "piscar" sem grupos.
const DISTINCTIVE_ADMIN_ROUTES = [
  'Minha conta', 'Marca', 'Tela de login', 'App Mobile', 'Produtos', 'Alunos', 'Comentários', 'Funcionalidades',
];

// Ícone (Octicons) por rota — cai no genérico quando a rota não estiver mapeada.
const ROUTE_ICONS = {
  Conteudo: 'home',
  Aplicativo: 'home',
  Marca: 'paintbrush',
  'Tela de login': 'sign-in',
  'App Mobile': 'device-mobile',
  'Minha conta': 'person',
  'Termos de Uso': 'law',
  Funcionalidades: 'gear',
  Produtos: 'package',
  Comentários: 'comment-discussion',
  Alunos: 'people',
  'Meu Perfil': 'person',
  'Meus Favoritos': 'heart',
  'Fazer Login': 'sign-in',
};

// Rótulos amigáveis quando o nome da rota não fica bom no menu.
const ROUTE_LABELS = {
  Conteudo: 'Início',
};

export function DrawerContent(props) {
  // Sessão e documento `users/{uid}` vêm do AuthUserProvider — o drawer monta
  // em toda tela e antes refazia essa leitura sozinho.
  const { user, isLogged: login } = useAuthUser();

  const logout = () => {
    signOut(auth)
      .then(() => {
        window.location.assign('../About');
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const { customColor, customFont } = useCustomTheme();

  // Tokens visuais derivados da MARCA DO CLIENTE (nunca da paleta neutra do admin).
  const textColor = customColor?.text?.app || THEME.COLORS.DRAWER_SELECTED;
  const bgColor = customColor?.background?.app || THEME.COLORS.BACKGROUND_ABOUT;
  const fontText = customFont?.fontFamilyText || THEME.FONTFAMILY.REGULAR;
  const fontTitle = customFont?.fontFamilyTitle || THEME.FONTFAMILY.BOLD;

  const mutedColor = hexToRgba(textColor, 0.6) || textColor;
  const hoverBg = hexToRgba(textColor, 0.1) || 'rgba(127, 127, 127, 0.12)';
  const dividerColor = hexToRgba(textColor, 0.14) || 'rgba(127, 127, 127, 0.22)';
  const avatarBg = hexToRgba(textColor, 0.16) || 'rgba(127, 127, 127, 0.2)';

  const routes = props.state?.routes || [];
  const activeKey = routes[props.state?.index]?.key;
  const isAdminDrawer = routes.some((r) => DISTINCTIVE_ADMIN_ROUTES.includes(r.name));
  const routeByName = routes.reduce((acc, r) => {
    acc[r.name] = r;
    return acc;
  }, {});

  const navigateTo = (route) => {
    props.navigation.dispatch({
      ...CommonActions.navigate({ name: route.name, merge: true }),
      target: props.state.key,
    });
  };

  const renderItem = (route) => {
    if (!route) return null;
    const focused = route.key === activeKey;
    const label = ROUTE_LABELS[route.name] || route.name;
    const iconName = ROUTE_ICONS[route.name] || 'dot-fill';
    return (
      <Pressable
        key={route.key}
        onPress={() => navigateTo(route)}
        style={({ hovered }) => [
          styles.row,
          focused && { backgroundColor: textColor },
          !focused && hovered && { backgroundColor: hoverBg },
        ]}
      >
        <Octicons name={iconName} size={16} color={focused ? bgColor : textColor} />
        <Text
          numberOfLines={1}
          style={[styles.rowLabel, { color: focused ? bgColor : textColor, fontFamily: fontText }]}
        >
          {label}
        </Text>
      </Pressable>
    );
  };

  // Item de link externo dentro de um grupo (ex.: "Central de ajuda").
  const renderExternalLink = (link) => (
    <A key={link.url} href={link.url} target="_blank" style={{ textDecorationLine: 'none' }}>
      <View style={styles.row}>
        <Octicons name={link.icon || 'link-external'} size={17} color={textColor} />
        <Text
          numberOfLines={1}
          style={[styles.rowLabel, { color: textColor, fontFamily: fontText }]}
        >
          {link.title}
        </Text>
      </View>
    </A>
  );

  const SectionHeader = ({ title }) => (
    <Text style={[styles.sectionHeader, { color: mutedColor, fontFamily: fontText }]}>{title}</Text>
  );

  // Itens fora de qualquer grupo (ex.: "Início") aparecem no topo, sem cabeçalho.
  const groupedNames = new Set(ADMIN_GROUPS.flatMap((g) => g.items));
  const leadingRoutes = routes.filter((r) => !groupedNames.has(r.name));

  const initial = (user?.Nome_Completo || user?.Email || '?').trim().charAt(0).toUpperCase();
  const hasExtraLinks =
    drawerOpenedLinks.length > 0 || (user && !user.isAdmin && drawerClosedLinks.length > 0);

  return (
    <Container background={bgColor}>
      <DrawerContentScrollView {...props} contentContainerStyle={styles.scrollContent}>
        {/* Cabeçalho com o usuário logado */}
        {login && user ? (
          <View style={[styles.profile, { borderBottomColor: dividerColor }]}>
            <View style={[styles.avatar, { backgroundColor: avatarBg }]}>
              <Text style={[styles.avatarText, { color: textColor, fontFamily: fontTitle }]}>
                {initial}
              </Text>
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text
                numberOfLines={1}
                style={[styles.profileName, { color: textColor, fontFamily: fontTitle }]}
              >
                {user.Nome_Completo || (user.isAdmin ? 'Administrador(a)' : 'Aluno(a)')}
              </Text>
              {user.Email ? (
                <Text
                  numberOfLines={1}
                  style={[styles.profileEmail, { color: mutedColor, fontFamily: fontText }]}
                >
                  {user.Email}
                </Text>
              ) : null}
            </View>
          </View>
        ) : null}

        {/* Menu — agrupado no admin, plano no aluno */}
        {isAdminDrawer ? (
          <>
            {leadingRoutes.map(renderItem)}
            {ADMIN_GROUPS.map((group) => {
              const groupRoutes = group.items.map((name) => routeByName[name]).filter(Boolean);
              const groupLinks = group.links || [];
              if (groupRoutes.length === 0 && groupLinks.length === 0) return null;
              return (
                <View key={group.title}>
                  <SectionHeader title={group.title} />
                  {groupRoutes.map(renderItem)}
                  {groupLinks.map(renderExternalLink)}
                </View>
              );
            })}
          </>
        ) : (
          routes.map(renderItem)
        )}

        {/* Links externos configuráveis pelo cliente */}
        {hasExtraLinks ? (
          <>
            <View style={[styles.divider, { backgroundColor: dividerColor }]} />
            {drawerOpenedLinks.map((item, index) => (
              <A key={`opened-link-${index}`} href={item.link} style={{ textDecorationLine: 'none' }}>
                <View style={styles.row}>
                  <Octicons name="link-external" size={17} color={textColor} />
                  <Text
                    numberOfLines={1}
                    style={[styles.rowLabel, { color: textColor, fontFamily: fontText }]}
                  >
                    {item.title}
                  </Text>
                </View>
              </A>
            ))}
            {user &&
              !user.isAdmin &&
              drawerClosedLinks.map((item, index) => (
                <Pressable
                  key={`closed-link-${index}`}
                  onPress={() => Linking.openURL(item.link)}
                  style={({ hovered }) => [styles.row, hovered && { backgroundColor: hoverBg }]}
                >
                  <Octicons name="link-external" size={17} color={textColor} />
                  <Text
                    numberOfLines={1}
                    style={[styles.rowLabel, { color: textColor, fontFamily: fontText }]}
                  >
                    {item.title}
                  </Text>
                </Pressable>
              ))}
          </>
        ) : null}
      </DrawerContentScrollView>

      {/* Sair — fixo acima do rodapé */}
      {login ? (
        <>
          <View style={[styles.divider, { backgroundColor: dividerColor, marginHorizontal: 14 }]} />
          <Pressable
            onPress={logout}
            style={({ hovered }) => [
              styles.row,
              { marginBottom: 8 },
              hovered && { backgroundColor: hoverBg },
            ]}
          >
            <Octicons name="sign-out" size={17} color={textColor} />
            <Text style={[styles.rowLabel, { color: textColor, fontFamily: fontText }]}>Sair</Text>
          </Pressable>
        </>
      ) : null}

      {VERSION_LABEL ? (
          <Text
            style={{
              textAlign: 'center',
              color: mutedColor,
              fontFamily: fontText,
              fontSize: 10,
              paddingBottom: 10,
            }}
          >
            {VERSION_LABEL}
          </Text>
      ) : null}
    </Container>
  );
}

const styles = StyleSheet.create({
  scrollContent: {
    paddingTop: 4,
  },
  profile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 14,
    paddingTop: 2,
    paddingBottom: 12,
    marginBottom: 2,
    borderBottomWidth: 1,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontSize: 16,
    fontWeight: '700',
  },
  profileName: {
    fontSize: 14,
    fontWeight: '700',
  },
  profileEmail: {
    fontSize: 12,
    marginTop: 1,
  },
  sectionHeader: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginTop: 14,
    marginBottom: 3,
    marginHorizontal: 18,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 9,
    paddingHorizontal: 14,
    borderRadius: 10,
    marginHorizontal: 8,
    marginBottom: 2,
    cursor: 'pointer',
  },
  rowLabel: {
    fontSize: 12,
    flex: 1,
    minWidth: 0,
  },
  divider: {
    height: 1,
    marginVertical: 5,
    marginHorizontal: 8,
  },
});
